import React, { useState } from 'react';
import { styled, StyledProps } from '@glitz/react';
import { translate } from '@polarnopyret/scope';
import connect from 'Shared/connect';
import Market from 'AppShell/Models/Market.type';
import DropdownBody from 'Shared/Dropdown/DropdownBody';
import DropdownShell from 'Shared/Dropdown/DropdownShell';
import * as style from 'Shared/Style';
import { ButtonHeight, NonInteractiveSolidButton } from 'Shared/TextButtons';
import currentPageIsCheckout from '../Checkout/Pages/Checkout/current-page-is-checkout';

import SizeViewModel from './SizeViewModel.type';
import { isCompact } from 'Shared/Viewport';
import SizeBaseViewModel from './SizeBaseViewModel.type';
import { PageType } from 'Shared/State';
import currentPageIsVariation from './current-page-is-variation';

const SizeTagsWrap = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '8px',
  flexWrap: 'wrap'
});

const SizeWrap = styled.span({
  textDecoration: 'line-through'
});

const StyledDropdownShell = styled(DropdownShell, {
  width: '100%',
  backgroundColor: style.colors.backgrounds.lighterGrey,
});

const SizeTagsContainer = styled(({ children }: { children: React.ReactNode } & StyledProps) => {
  return (
    <SizeTagsWrap>
      {children}
    </SizeTagsWrap>
  );
});

type SelectableSizesType = {
  sizes: Array<SizeViewModel | SizeBaseViewModel>;
  selectedSizeCode: string;
  setSelectedSize: (size: SizeViewModel | SizeBaseViewModel, name?: string | React.ReactNode) => void;
  placeholder: string;
  market: Market;
  currentBreakpoint: number;
  currentPage: PageType;
  sizeBackupStoreAvailabilities?: { [item: string]: boolean };
  notifyMe: boolean;
  evaluateInStock?: boolean;
};

const MARKETS_USING_AGE_AS_SIZE = ['GB', 'DE'];
const USE_SIZE_AS_DISPLAY_NAME = true;

const SelectableSizesInternal = function ({
  sizes,
  selectedSizeCode,
  setSelectedSize,
  placeholder,
  market,
  currentBreakpoint,
  currentPage,
  sizeBackupStoreAvailabilities,
  notifyMe,
  evaluateInStock = true,
}: SelectableSizesType) {
  const [displayName, setDisplayName] = useState<string | React.ReactNode>(() => {
    if (!USE_SIZE_AS_DISPLAY_NAME) return placeholder;

    const size = sizes.find((size) => size.code === selectedSizeCode);
    if (!size) return placeholder;

    return getSizeDisplayName(size);
  });

  // const [outOfStock, setOutOfStock] = useState<boolean>(() => {
  //   const size = sizes.find((size) => size.code === selectedSizeCode);
  //   if (!size) return false;

  //   return size.totalAvailable === 0;
  // });

  function isAgeAsSizePrimary(market: Market) {
    return MARKETS_USING_AGE_AS_SIZE.includes(market.id);
  }

  function getSizeDisplayName(size: SizeViewModel | SizeBaseViewModel) {
    if (!size.sizeAge || size.sizeAge === '') {
      return size.sizeName;
    }

    const primarySize = isAgeAsSizePrimary(market) ? size.sizeAge : size.sizeName;
    const secondarySize = isAgeAsSizePrimary(market) ? size.sizeName : size.sizeAge;
    return size.totalAvailable === 0 ? <SizeWrap>{`${primarySize} (${secondarySize})`}</SizeWrap> : <>{`${primarySize} (${secondarySize})`}</>;
  }

  function onSizeChange(sizeCode: string) {
    const newSize = sizes.find((size) => size.code === sizeCode);
    if (newSize) {
      setSelectedSize(newSize, getSizeDisplayName(newSize));
      if (USE_SIZE_AS_DISPLAY_NAME) {
        //setOutOfStock(newSize.totalAvailable === 0);
        setDisplayName(getSizeDisplayName(newSize))
      };
    }
  }

  const isMobile = isCompact(currentBreakpoint);
  const options = sizes.map((size) => {
    const primarySize = size.sizeAge && size.sizeAge !== '' && isAgeAsSizePrimary(market) ? size.sizeAge : size.sizeName;
    const secondarySize = isAgeAsSizePrimary(market) ? size.sizeName : size.sizeAge;

    const availableInBackupStore = sizeBackupStoreAvailabilities ? sizeBackupStoreAvailabilities[size.code] : false;

    let isDisabled = false;
    let disabledSelectable = false;
    const inStock = (size as SizeViewModel).totalAvailable > 0 || availableInBackupStore;

    if (evaluateInStock) {
      if ('inStock' in size) isDisabled = !inStock;
      else if ('stockAvailability' in size) isDisabled = (size as SizeViewModel).stockAvailability === 'none';
      else isDisabled = size.totalAvailable === 0;
    } else {
      if ('inStock' in size) disabledSelectable = !inStock;
      else if ('stockAvailability' in size) disabledSelectable = (size as SizeViewModel).stockAvailability === 'none';
      else disabledSelectable = size.totalAvailable === 0;
    }

    let secondaryText = '';
    if (notifyMe && (size as SizeViewModel).stockAvailability === 'none') {
      secondaryText = ` ${translate('/site/product/notifyme/label')}`;
    }

    return {
      text: (
        <>
          <b>{primarySize}</b> {secondarySize && secondarySize !== '' && `(${secondarySize})`}
        </>
      ),
      secondaryText: secondaryText,
      value: size.code,
      disabled: isDisabled,
      disabledSelectable: disabledSelectable,
    };
  });

  return (
    <SizeTagsContainer>
      <StyledDropdownShell
        css={{
          border: {
            radius: '16px',
          },
        }}
        displayNameStyle={{
          paddingLeft: '4px'
        }}
        displayName={displayName}
        closeOnClickInContent={!isMobile}
        noTopMargin
        noOpenBorderBottom
        useModal={isMobile}
        borderWidth="medium"
        borderRadius="8px"
        withPortal={currentPageIsCheckout(currentPage) || currentPageIsVariation(currentPage)}
      >
        <DropdownBody
          ulStyle={{
            backgroundColor: style.colors.backgrounds.lighterGrey,
            gap: '8px',
            borderTop: {
              x: {
                radius: 0,
              },
            },
            overflowY: 'auto',
            ...(isMobile
              ? {
                border: {
                  xy: {
                    width: 0,
                  },
                },
                maxHeight: '400px',
              }
              : {
                border: {
                  xy: {
                    width: 'medium',
                  },
                  top: {
                    width: '0',
                  },
                  radius: '8px',
                },
                maxHeight: '310px',
              }),
          }}
          liStyle={{
            backgroundColor: style.colors.backgrounds.white,
            padding: {
              y: '24px',
              x: '12px',
            },
          }}
          textStyle={{
            padding: {
              x: '16px',
            },
          }}
          hideCount
          options={options}
          onChangeOption={onSizeChange}
          value={selectedSizeCode}
        />
      </StyledDropdownShell>
    </SizeTagsContainer >
  );
};

export const SelectableSizes = connect((state) => ({
  market: state.appShellData.market,
  currentBreakpoint: state.currentBreakpoint,
  currentPage: state.currentPage,
}))(SelectableSizesInternal);

type AvailableSizesType = {
  availableSizes: { [item: string]: boolean };
};

export const AvailableSizes = function ({ availableSizes }: AvailableSizesType) {
  return (
    <SizeTagsContainer>
      {Object.entries(availableSizes).map((entry, index) => {
        const [size, isAvailable] = entry;
        return (
          <NonInteractiveSolidButton
            key={index}
            text={size}
            height={ButtonHeight.Small}
            disabled={!isAvailable}
            padding={{ x: 12 }}
            fontSize={12}
          />
        );
      })}
    </SizeTagsContainer>
  );
};
